import React, { Suspense, lazy } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { I18nProvider } from './lib/i18n'
import App from './App'
import './index.css'

// Dynamic import of all subpages from /pages/ folder (excluding admin folder)
const pageModules = import.meta.glob('./pages/*.tsx')

// Dynamic import of admin pages
const adminPageModules = import.meta.glob('./pages/admin/*.tsx')

const subpageRoutes = Object.entries(pageModules).map(([path, importFn]) => {
  const slug = path.replace('./pages/', '').replace('.tsx', '')
  const Component = lazy(importFn as () => Promise<{ default: React.ComponentType }>)
  return { path: '/' + slug, element: <Component /> }
})

// Admin routes
const adminRoutes = Object.entries(adminPageModules).map(([path, importFn]) => {
  const slug = path.replace('./pages/admin/', '').replace('.tsx', '')
  const Component = lazy(importFn as () => Promise<{ default: React.ComponentType }>)
  // Handle index.tsx as /admin root
  const routePath = slug === 'index' ? '/admin' : '/admin/' + slug
  return { path: routePath, element: <Component /> }
})

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin w-8 h-8 border-2 border-current border-t-transparent rounded-full" />
  </div>
)

// Error boundary for runtime React errors - logs only, no UI
// Vite's native error overlay handles build/TypeScript errors
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      // Show minimal error message
      return (
        <div style={{
          minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#0a0a0a', color: '#ef4444', fontFamily: 'monospace', padding: '2rem'
        }}>
          <div style={{ maxWidth: '600px', textAlign: 'center' }}>
            <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Runtime Error</h1>
            <pre style={{ textAlign: 'left', background: '#1a1a1a', padding: '1rem', borderRadius: '8px', overflow: 'auto' }}>
              {this.state.error?.message}
            </pre>
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: '1.5rem', padding: '0.75rem 1.5rem', background: '#ef4444',
                color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem'
              }}
            >Reload</button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

// Main App - use Vite's native error overlay
function Root() {
  return (
    <ErrorBoundary>
      <I18nProvider>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<App />} />
            {subpageRoutes.map(route => (
              <Route key={route.path} path={route.path} element={route.element} />
            ))}
            {adminRoutes.map(route => (
              <Route key={route.path} path={route.path} element={route.element} />
            ))}
            {/* Dynamic admin booking detail route */}
            <Route path="/admin/bookings/:id" element={
              <Suspense fallback={<PageLoader />}>
                {(() => {
                  const Component = lazy(() => import('./pages/admin/booking-detail'))
                  return <Component />
                })()}
              </Suspense>
            } />
            {/* Catch-all for admin routes - redirect to login */}
            <Route path="/admin/*" element={
              <Suspense fallback={<PageLoader />}>
                {(() => {
                  const Component = lazy(() => import('./pages/admin/login'))
                  return <Component />
                })()}
              </Suspense>
            } />
          </Routes>
        </Suspense>
      </BrowserRouter>
      </I18nProvider>
    </ErrorBoundary>
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
)